import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    endTutorial,
    nextTutorialStep,
    previousTutorialStep,
    skipTutorial,
    subscribeTutorial,
    type TutorialStep,
} from '@/services/tutorial.service';
import { CotinButton } from '@cotin/biblioteca-componentes-react';

const CotinTutorial: React.FC = () => {
    const [isActive, setIsActive] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [totalSteps, setTotalSteps] = useState(0);
    const [currentStepData, setCurrentStepData] = useState<TutorialStep | null>(null);
    const [spotlightPosition, setSpotlightPosition] = useState({
        top: '0px',
        left: '0px',
        width: '0px',
        height: '0px',
    });
    const [tooltipPosition, setTooltipPosition] = useState({
        top: '0px',
        left: '0px',
    });

    const reflowThrottleRef = useRef(false);

    const calculateTooltipPosition = useCallback((rect: DOMRect, position: TutorialStep['position']) => {
        const tooltipWidth = 320;
        const tooltipHeight = 200;
        const margin = 20;
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;

        let top = 0;
        let left = 0;
        const pos = position || 'bottom';

        switch (pos) {
            case 'top':
                top = rect.top - tooltipHeight - margin;
                left = rect.left + rect.width / 2 - tooltipWidth / 2;
                break;
            case 'bottom':
                top = rect.bottom + margin;
                left = rect.left + rect.width / 2 - tooltipWidth / 2;
                break;
            case 'left':
                top = rect.top + rect.height / 2 - tooltipHeight / 2;
                left = rect.left - tooltipWidth - margin;
                break;
            case 'right':
                top = rect.top + rect.height / 2 - tooltipHeight / 2;
                left = rect.right + margin;
                break;
        }

        if (left < margin) left = margin;
        if (left + tooltipWidth > windowWidth - margin) {
            left = windowWidth - tooltipWidth - margin;
        }
        if (top < margin) top = margin;
        if (top + tooltipHeight > windowHeight - margin) {
            top = windowHeight - tooltipHeight - margin;
        }

        setTooltipPosition({
            top: `${top}px`,
            left: `${left}px`,
        });
    }, []);

    const isMostlyInsideViewport = useCallback((rect: DOMRect, margin: number) => {
        const w = window.innerWidth;
        const h = window.innerHeight;
        return (
            rect.top >= -margin &&
            rect.left >= -margin &&
            rect.bottom <= h + margin &&
            rect.right <= w + margin
        );
    }, []);

    const measureTutorialStep = useCallback(
        (stepData: TutorialStep, attemptScroll: boolean): boolean => {
            const sel = stepData.selector;
            if (!sel) return false;

            const el = document.querySelector(sel);
            if (!el || !(el instanceof HTMLElement)) {
                console.warn(`Elemento não encontrado: ${sel}`);
                return false;
            }

            let rect = el.getBoundingClientRect();
            if (attemptScroll && !isMostlyInsideViewport(rect, 24)) {
                el.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
                rect = el.getBoundingClientRect();
            }

            const padding = 8;
            setSpotlightPosition({
                top: `${Math.round(rect.top - padding)}px`,
                left: `${Math.round(rect.left - padding)}px`,
                width: `${Math.round(rect.width + padding * 2)}px`,
                height: `${Math.round(rect.height + padding * 2)}px`,
            });
            calculateTooltipPosition(rect, stepData.position);
            return true;
        },
        [calculateTooltipPosition, isMostlyInsideViewport]
    );

    const updatePositions = useCallback(
        (stepData: TutorialStep | null) => {
            if (!stepData) return;
            const measured = measureTutorialStep(stepData, true);
            if (!measured) return;
            requestAnimationFrame(() => {
                requestAnimationFrame(() => measureTutorialStep(stepData, false));
            });
        },
        [measureTutorialStep]
    );

    useEffect(() => {
        const unsubscribe = subscribeTutorial((state) => {
            setIsActive(state.isActive);
            setCurrentStep(state.currentStep);
            setTotalSteps(state.steps.length);

            if (state.isActive && state.steps.length > 0) {
                const step = state.steps[state.currentStep];
                setCurrentStepData(step);
                queueMicrotask(() => updatePositions(step));
            } else {
                setCurrentStepData(null);
            }
        });
        return unsubscribe;
    }, [updatePositions]);

    useEffect(() => {
        if (!isActive || !currentStepData) return;

        const scheduleReflow = () => {
            if (reflowThrottleRef.current) return;
            reflowThrottleRef.current = true;
            requestAnimationFrame(() => {
                reflowThrottleRef.current = false;
                measureTutorialStep(currentStepData, false);
            });
        };

        window.addEventListener('resize', scheduleReflow);
        window.addEventListener('scroll', scheduleReflow, true);
        return () => {
            window.removeEventListener('resize', scheduleReflow);
            window.removeEventListener('scroll', scheduleReflow, true);
        };
    }, [isActive, currentStepData, measureTutorialStep]);

    const isFirstStep = currentStep === 0;
    const isLastStep = currentStep === totalSteps - 1;

    if (!isActive || typeof document === 'undefined') {
        return null;
    }

    const overlay = (
        <div className="tutorial-overlay">
            <div className="tutorial-backdrop" onClick={skipTutorial} role="presentation" />

            <div
                className="tutorial-spotlight"
                style={{
                    top: spotlightPosition.top,
                    left: spotlightPosition.left,
                    width: spotlightPosition.width,
                    height: spotlightPosition.height,
                }}
            />

            {currentStepData && (
                <div
                    className="tutorial-tooltip"
                    style={{
                        top: tooltipPosition.top,
                        left: tooltipPosition.left,
                    }}>
                    <div className="tooltip-header">
                        <h3 className="tooltip-title">{currentStepData.title}</h3>
                        <button
                            type="button"
                            className="tooltip-close"
                            onClick={skipTutorial}
                            aria-label="Fechar tutorial">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M18 6L6 18M6 6l12 12"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </button>
                    </div>

                    <div className="tooltip-content">
                        <p className="tooltip-description">{currentStepData.description}</p>
                    </div>

                    <div className="tooltip-footer">
                        <div className="tooltip-progress">
                            <span className="progress-text">
                                {currentStep + 1} de {totalSteps}
                            </span>
                            <div className="progress-dots">
                                {Array.from({ length: totalSteps }).map((_, index) => (
                                    <span
                                        key={index}
                                        className={`progress-dot ${index === currentStep ? 'active' : ''}`}
                                    />
                                ))}
                            </div>
                        </div>

                        <div className="tooltip-actions">
                            <CotinButton
                                id="btn-tutorial-pular"
                                type="button"
                                variant="default"
                                size="medium"
                                text="Pular"
                                onClick={skipTutorial}
                            />

                            <div className="navigation-buttons">
                                {!isFirstStep && (
                                    <CotinButton
                                        id="btn-tutorial-anterior"
                                        type="button"
                                        variant="default"
                                        size="medium"
                                        text="Anterior"
                                        onClick={previousTutorialStep}
                                    />
                                )}

                                <CotinButton
                                    id="btn-tutorial-proximo"
                                    type="button"
                                    variant="primary"
                                    size="medium"
                                    text={isLastStep ? 'Finalizar' : 'Próximo'}
                                    onClick={isLastStep ? endTutorial : nextTutorialStep}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    return createPortal(overlay, document.body);
};

export default CotinTutorial;
